import { StrictMode, Suspense } from 'react'
import { createRoot } from 'react-dom/client'
import { createBrowserRouter, RouterProvider } from 'react-router-dom'
import './i18n'
import './index.css'
import App from './App.jsx'
import LoadingSpinner from './components/LoadingSpinner.jsx'
import HomePage from './pages/HomePage.jsx'
import AboutPage from './pages/AboutPage.jsx'
import ArchitecturePage from './pages/ArchitecturePage.jsx'
import ConstructionPage from './pages/ConstructionPage.jsx'
import ImpactWindowsPage from './pages/ImpactWindowsPage.jsx'
import RoofingPage from './pages/RoofingPage.jsx'
import PrivacyPolicy from './pages/PrivacyPolicy.jsx'
import ContactPage from './pages/ContactPage.jsx'
import SuccessPage from './pages/SuccessPage.jsx'
import BlogList from './pages/blog/BlogList.jsx'
import BlogPost from './pages/blog/BlogPost.jsx'
import AdminDashboard from './pages/admin/AdminDashboard.jsx'
import SitemapPage from './pages/SitemapPage.jsx'
import StoreHome from './pages/store/StoreHome.jsx'
import StoreItemDetails from './pages/store/StoreItemDetails.jsx'
import StorageManagement from './pages/admin/StorageManagement.jsx'
import GalleryEditor from './pages/admin/GalleryEditor.jsx'
import ArchQuickCalculator from './pages/admin/ArchQuickCalculator.jsx'
import ArchitectureNewApplicationPage from './pages/forms/ArchitectureNewApplicationPage.jsx'
import ArchitectureNewScopePage from './pages/forms/ArchitectureNewScopePage.jsx'
import ConstructionNewProjectPage from './pages/forms/ConstructionNewProjectPage.jsx'
import RoofingNewProjectPage from './pages/forms/RoofingNewProjectPage.jsx'
import ImpactWindowsNewProjectPage from './pages/forms/ImpactWindowsNewProjectPage.jsx'

import ErrorBoundary from './components/ErrorBoundary.jsx'
import RoofLanding from './pages/landings/RoofLanding.jsx'
import ConcreteLanding from './pages/landings/ConcreteLanding.jsx'
import PergolaLanding from './pages/landings/PergolaLanding.jsx'
import RoofLandingForm from './pages/landings/RoofLandingForm.jsx'
import ConcreteLandingForm from './pages/landings/ConcreteLandingForm.jsx'
import PergolaLandingForm from './pages/landings/PergolaLandingForm.jsx'
import RoofLandingThankYou from './pages/landings/RoofLandingThankYou.jsx'
import ConcreteLandingThankYou from './pages/landings/ConcreteLandingThankYou.jsx'
import PergolaLandingThankYou from './pages/landings/PergolaLandingThankYou.jsx'
import ArchitectureNewQuoteThankYou from './pages/forms/ArchitectureNewQuoteThankYou.jsx'
import ArchitectureNewScopeThankYou from './pages/forms/ArchitectureNewScopeThankYou.jsx'
import ConstructionNewQuoteThankYou from './pages/forms/ConstructionNewQuoteThankYou.jsx'
import RoofingNewQuoteThankYou from './pages/forms/RoofingNewQuoteThankYou.jsx'
import ImpactWindowsNewQuoteThankYou from './pages/forms/ImpactWindowsNewQuoteThankYou.jsx'

// Subdomain logic
const hostname = window.location.hostname
const subdomain = hostname.split('.')[0]

const getIndexElement = () => {
  if (subdomain === 'store') return <StoreHome />
  if (subdomain === 'construction') return <ConstructionPage />
  if (subdomain === 'architecture') return <ArchitecturePage />
  if (subdomain === 'windows') return <ImpactWindowsPage />
  if (subdomain === 'roofing') return <RoofingPage />
  if (subdomain === 'contact') return <ContactPage />
  return <HomePage />
}

const router = createBrowserRouter([
  { path: 'landing-roof', element: <RoofLanding /> },
  { path: 'landing-concrete', element: <ConcreteLanding /> },
  { path: 'landing-pergola', element: <PergolaLanding /> },
  { path: 'landing-roof-form', element: <RoofLandingForm /> },
  { path: 'landing-concrete-form', element: <ConcreteLandingForm /> },
  { path: 'landing-pergola-form', element: <PergolaLandingForm /> },
  { path: 'landing-roof-form/thank-you', element: <RoofLandingThankYou /> },
  { path: 'landing-concrete-form/thank-you', element: <ConcreteLandingThankYou /> },
  { path: 'landing-pergola-form/thank-you', element: <PergolaLandingThankYou /> },
  {
    path: '/',
    element: <App />,
    errorElement: <ErrorBoundary />,
    children: [
      { index: true, element: getIndexElement() },
      { path: 'home', element: <HomePage /> },
      { path: 'about', element: <AboutPage /> },
      { path: 'architecture', element: <ArchitecturePage /> },
      { path: 'construction', element: <ConstructionPage /> },
      { path: 'impact-windows', element: <ImpactWindowsPage /> },
      { path: 'windows', element: <ImpactWindowsPage /> },
      { path: 'roofing', element: <RoofingPage /> },
      { path: 'privacy-policy', element: <PrivacyPolicy /> },
      { path: 'terms-of-service', element: <PrivacyPolicy /> },
      { path: 'contact', element: <ContactPage /> },
      { path: 'success', element: <SuccessPage /> },
      { path: 'blog', element: <BlogList /> },
      { path: 'blog/:slug', element: <BlogPost /> },
      { path: 'store', element: <StoreHome /> },
      { path: 'store/:planNumber', element: <StoreItemDetails /> },
      { path: 'shop', element: <StoreHome /> },
      { path: 'shop/:planNumber', element: <StoreItemDetails /> },
      { path: 'admin', element: <AdminDashboard /> },
      { path: 'admin/blog', element: <AdminDashboard /> },
      { path: 'admin/products', element: <AdminDashboard /> },
      { path: 'admin/portfolio', element: <AdminDashboard /> },
      { path: 'admin/monday', element: <AdminDashboard /> },
      { path: 'admin/workflow', element: <AdminDashboard /> },
      { path: 'admin/estimator', element: <AdminDashboard /> },
      { path: 'admin/console', element: <AdminDashboard /> },
      { path: 'admin/storage', element: <StorageManagement /> },
      { path: 'admin/gallery', element: <GalleryEditor /> },
      { path: 'admin/arch-calculator', element: <ArchQuickCalculator /> },
      { path: 'sitemap', element: <SitemapPage /> },
      {
        path: 'forms',
        children: [
          { index: true, element: <ArchitectureNewApplicationPage /> },
          { path: 'architecture/new-quote', element: <ArchitectureNewApplicationPage /> },
          { path: 'architecture/new-scope', element: <ArchitectureNewScopePage /> },
          { path: 'construction/new-quote', element: <ConstructionNewProjectPage /> },
          { path: 'roofing/new-quote', element: <RoofingNewProjectPage /> },
          { path: 'impact-windows/new-quote', element: <ImpactWindowsNewProjectPage /> },
          { path: 'architecture/new-quote/thank-you', element: <ArchitectureNewQuoteThankYou /> },
          { path: 'architecture/new-scope/thank-you', element: <ArchitectureNewScopeThankYou /> },
          { path: 'construction/new-quote/thank-you', element: <ConstructionNewQuoteThankYou /> },
          { path: 'roofing/new-quote/thank-you', element: <RoofingNewQuoteThankYou /> },
          { path: 'impact-windows/new-quote/thank-you', element: <ImpactWindowsNewQuoteThankYou /> },
        ],
      },
    ],
  },
])

createRoot(document.getElementById('root')).render(
  <StrictMode>
    <Suspense fallback={<LoadingSpinner />}>
      <RouterProvider router={router} />
    </Suspense>
  </StrictMode>,
)

// ── SW update banner (injected before React hydration completes) ─────────────
function showUpdateBanner(version, note) {
  if (document.getElementById('sw-update-banner')) return // already shown
  const bar = document.createElement('div')
  bar.id = 'sw-update-banner'
  bar.style.cssText = [
    'position:fixed', 'bottom:0', 'left:0', 'right:0', 'z-index:99999',
    'background:#111827', 'color:#f9fafb', 'font-family:system-ui,sans-serif',
    'font-size:13px', 'display:flex', 'align-items:center', 'justify-content:center',
    'gap:12px', 'padding:14px 16px',
    'box-shadow:0 -2px 12px rgba(0,0,0,.3)',
    'border-top:2px solid #fbbf24',
    'animation:slideUp .25s ease-out',
  ].join(';')
  const styleEl = document.createElement('style')
  styleEl.textContent = '@keyframes slideUp{from{transform:translateY(100%)}to{transform:translateY(0)}}'
  document.head.appendChild(styleEl)

  const msg = document.createElement('span')
  let text = '✨ Nueva versión disponible'
  if (version) text += ` — v${version}`
  if (note) {
    text += ' - ' + note
    msg.style.whiteSpace = 'pre-line'
  }
  msg.textContent = text
  msg.style.cssText = 'max-width:60vw;line-height:1.5;'

  const btn = document.createElement('button')
  btn.textContent = 'Actualizar ahora'
  btn.style.cssText = [
    'background:#fbbf24', 'color:#111827', 'border:none', 'border-radius:6px',
    'padding:4px 12px', 'font-size:12px', 'font-weight:700', 'cursor:pointer',
    'flex-shrink:0',
  ].join(';')
  btn.onclick = () => window.location.reload()
  const dismiss = document.createElement('button')
  dismiss.textContent = '✕'
  dismiss.style.cssText = 'background:transparent;border:none;color:#9ca3af;cursor:pointer;font-size:14px;padding:2px 6px;flex-shrink:0;'
  dismiss.onclick = () => bar.remove()
  bar.append(msg, btn, dismiss)
  document.body.appendChild(bar)
}

if ('serviceWorker' in navigator) {
  if (import.meta.env.PROD) {
    window.addEventListener('load', () => {
      navigator.serviceWorker.register('/sw.js').catch((error) => {
        console.error('Service worker registration failed:', error)
      })
    })
    // Listen for the APP_UPDATED broadcast from the new SW on activate
    navigator.serviceWorker.addEventListener('message', (event) => {
      if (event.data?.type === 'APP_UPDATED') {
        const isAdmin = window.location.pathname.startsWith('/admin')
        const isStandalone = window.matchMedia('(display-mode: standalone)').matches
        if (isAdmin || isStandalone) showUpdateBanner(event.data.version, event.data.note)
      }
    })
  } else {
    // Force unregister in development to avoid stale caching issues
    navigator.serviceWorker.getRegistrations().then((registrations) => {
      for (let registration of registrations) {
        registration.unregister()
        console.log('Unregistered service worker in dev mode')
        window.location.reload() // Reload once to apply changes
      }
    })
  }
}
